// Compute performance stats from a list of scored tickets.
// A ticket is "counted" only when it has a score object (from the Score
// button) — unscored tickets are excluded from win-rate and ROI so the
// numbers reflect only settled bets.

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

// Rolling windows: last-7 days and last-30 days.
const rollingWindow = (d, days) => {
  const to = new Date(d);
  const from = new Date(d.getTime() - days * 24 * 60 * 60 * 1000);
  return { from, to };
};

export const PERIODS = ["day", "week", "month", "all"];

export const periodLabel = (p) =>
  ({ day: "Today", week: "Last 7 Days", month: "Last 30 Days", all: "All Time" }[p] ||
    p);

// Wager-type filter for the Performance Report. "all" = every wager kind.
// Values match ticket.kind emitted by OptimalTicket / OptimalExoticsTicket.
export const KINDS = ["all", "DD", "Exacta", "Trifecta"];

export const kindLabel = (k) =>
  ({ all: "All Wagers", DD: "Daily Double", Exacta: "Exacta", Trifecta: "Trifecta" }[k] || k);

export const rangeFor = (period, now = new Date()) => {
  if (period === "day") return { from: startOfDay(now), to: new Date(now) };
  if (period === "week") return rollingWindow(now, 7);
  if (period === "month") return rollingWindow(now, 30);
  return { from: new Date(0), to: new Date(now) };
};

const inRange = (ts, from, to) => {
  const t = new Date(ts).getTime();
  return t >= from.getTime() && t <= to.getTime();
};

export const computeStats = (tickets, period, kind = "all") => {
  const { from, to } = rangeFor(period);
  const inWindow = (tickets || [])
    .filter((t) => inRange(t.ts, from, to))
    .filter((t) => (kind === "all" ? true : t.kind === kind));
  const scored = inWindow.filter((t) => t.score);
  const hits = scored.filter((t) => t.score.hit);

  const totalTickets = inWindow.length;
  const scoredTickets = scored.length;
  const hitCount = hits.length;

  const totalWagered = inWindow.reduce(
    (s, t) => s + Number(t.base || 0) * Number(t.combos || 0),
    0
  );
  const totalPayout = hits.reduce(
    (s, t) => s + Number(t.score?.payoff || 0),
    0
  );
  const totalNet = scored.reduce(
    (s, t) => s + Number(t.score?.net || 0),
    0
  );

  const avgTicket = totalTickets > 0 ? totalWagered / totalTickets : 0;
  const avgPayout = hitCount > 0 ? totalPayout / hitCount : 0;
  const payoutToTicketRatio =
    avgTicket > 0 && avgPayout > 0 ? avgPayout / avgTicket : 0;
  const winPct =
    scoredTickets > 0 ? (hitCount / scoredTickets) * 100 : 0;
  const roi = totalWagered > 0 ? (totalNet / totalWagered) * 100 : 0;

  return {
    period,
    from,
    to,
    totalTickets,
    scoredTickets,
    hitCount,
    totalWagered,
    totalPayout,
    totalNet,
    avgTicket,
    avgPayout,
    payoutToTicketRatio,
    winPct,
    roi,
  };
};
